import { GenericAbortSignal } from 'axios';

import Upload from 'hooks/useDirectUpload/Upload';
import { Paginated } from 'models/Paginated';
import Workflow, { WorkflowOptions } from 'models/Workflow';
import { WorkflowAction } from 'models/WorkflowAction';
import { WorkflowActionView } from 'models/WorkflowActionView';
import http from 'services/http';

// A map of declared value/output names -> value. The KEYS are snake_case domain
// identifiers (`patient_first_name`, `clinical_determination`, `final_outcome`) and
// must be sent verbatim — they are not API field names, so OliveBranch leaves them alone.
// Values are usually strings but may be structured (e.g. `candidate_group_ids: [id]`).
export type WorkflowValuesInput = Record<string, unknown>;

// One document in the packet: a file just uploaded, or one the referral already holds,
// named by its attachment id (OLIO-2773).
export type PacketEntry = Upload | { id: string };

// What actually goes on the wire. A new document is named by its signed_id; one the referral
// already holds is named by its attachment id. Never both — the API reads `id` first, so an entry
// carrying both would be looked up as an existing attachment and rejected.
type SerializedPacketEntry = { signedId: string; docType?: string; filename: string } | { id: string };

// The packet: the full set the referral should end up with, not a list of additions. The API
// mints Attachments for the new uploads and keeps the ones named by id, so a document left
// out is removed — which is how removing a file works.
//
// Absence and emptiness mean different things, so they can't be conflated. `undefined` omits
// the key: the packet is untouched. `[]` is sent: the person removed every document. Only a
// caller that knows the packet changed should pass a value at all.
function serializeAttachments(attachments?: PacketEntry[]): SerializedPacketEntry[] | undefined {
  if (!attachments) return undefined;

  return attachments.flatMap<SerializedPacketEntry>((entry) => {
    if (!(entry instanceof Upload)) return [{ id: entry.id }];
    // An upload still in flight has no signed_id yet — it isn't a document the API can
    // resolve, so it's left out rather than sent as a broken entry.
    if (!entry.signedId) return [];

    // Deliberately NOT upload.serialize(): that carries the upload's own client-side `id`, which
    // the API now reads as "a document this referral already holds". A new upload would be looked
    // up as an existing attachment, not found, and the whole save rejected.
    return [{ signedId: entry.signedId, docType: entry.docType, filename: entry.filename }];
  });
}

// The prefix every list variant's key extends — the websocket nudge in useWorkflows
// invalidates THIS (not one param-keyed key), so every open page/filter combination
// refetches regardless of which params it was fetched with.
const LIST_PREFIX = ['workflows', 'list'] as const;

// The portal surfaces' filter axis (OLIO-3019/3023) — a named subset of WorkflowsIndexParams
// kept for the Tasks / Portal Scans callers.
export interface WorkflowListParams {
  definitionApiName?: string[];
  status?: string[];
  // Opt-in paging (OLIO-3023): sending `page` is what flips the API into its paged mode.
  page?: number;
  pageSize?: number;
  // `column direction`, e.g. 'created_at desc' — the paged lists sort server-side.
  sortBy?: string;
}

export const workflowQueryKeys = {
  base: ['workflows'] as const,
  listPrefix: LIST_PREFIX,
  // Alias of listPrefix, kept for the portal surfaces — same prefix, so one invalidation
  // reaches every open list regardless of which name keyed it.
  lists: LIST_PREFIX,
  list: (params: Record<string, any> = {}) => [...LIST_PREFIX, params] as const,
  show: (id: string) => ['workflows', 'show', id] as const,
  logs: (id: string) => ['workflows', 'logs', id] as const,
};

// The list's status vocabulary as the API's `list_status` param spells it — the IN-FLIGHT
// pill states from ListStatus (referralProgress.ts). Deliberately a subset, and not a
// mirror: pending/complete/canceled are expressed through `status` instead (see
// WorkflowsIndexParams below), and `selected`/`notSelected` (OLIO-3172) are a THIRD axis —
// they come from the recorded referral outcome, which the API filters via `outcome`, not
// `list_status`. So do not add a ListStatus member here without checking which of the three
// params the API actually filters it on.
export type WorkflowListStatusParam = 'error' | 'action_needed' | 'ready_for_review' | 'processing';

// Everything GET /workflows accepts. Camel-cased (OliveBranch snake_cases keys on the way in);
// the `.not` suffix is sent literally alongside the base key, same as admissions'
// `criteriaEvaluationStatus.not` — the middleware inflects the base name and leaves the suffix
// alone. Arrays are sent as comma-joined strings (reviews.ts `serializeFilters` precedent), not
// axios's bracket array serialization.
export type WorkflowsIndexParams = {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  search?: string;
  status?: string | string[];
  'status.not'?: string | string[];
  outcome?: string;
  groupId?: string;
  // Range as a 2-element [start, end] — serialized the same bracketed-ISO shape
  // reviews.ts uses for `createdAt`.
  createdAt?: [Date, Date];
  listStatus?: WorkflowListStatusParam | WorkflowListStatusParam[];
  // The per-building queue: referrals with action-required work on a building this user holds.
  queue?: boolean;
  apiName?: string | string[];
  'apiName.not'?: string | string[];
  // Same axis as apiName (OLIO-3019) — kept for the portal surfaces' callers.
  definitionApiName?: string | string[];
  // The client's own referral source names (OLIO-3161). Sent as a real array, not comma-joined
  // like the params above: these are hospital names, and a hospital name can contain a comma.
  referralSource?: string[];
};

// Values that stay arrays on the wire because their members can contain a comma.
const UNJOINED_ARRAY_PARAMS = ['referralSource'];

function serializeWorkflowsParams({ createdAt, ...rest }: WorkflowsIndexParams): Record<string, unknown> {
  const params: Record<string, unknown> = {};
  Object.entries(rest).forEach(([key, value]) => {
    if (value === undefined) return;
    params[key] = Array.isArray(value) && !UNJOINED_ARRAY_PARAMS.includes(key) ? value.join(',') : value;
  });
  if (createdAt) {
    params.createdAt = `[${createdAt[0].toISOString()},${createdAt[1].toISOString()}]`;
  }
  return params;
}

// The inbox: workflows the user can see (allowed_groups scope). Pagination is opt-in on the
// API — passing page/pageSize gets back {data, meta, links}; without them it's a bare {data}
// (e.g. useWorkflows() called with no params). Either way this always resolves to the app's
// Paginated<Workflow> shape, synthesizing a single-page meta for the bare-shape case so callers
// never have to branch on it.
export async function indexWorkflows(
  params: WorkflowsIndexParams = {},
  signal?: GenericAbortSignal
): Promise<Paginated<Workflow>> {
  return http
    .get<{ data: WorkflowOptions[] } | Paginated<WorkflowOptions>>('workflows', {
      params: serializeWorkflowsParams(params),
      signal,
    })
    .then((res) => {
      const body = res.data;
      const data = body.data.map((w) => new Workflow(w));
      if ('meta' in body) {
        return { ...body, data };
      }
      return { data, meta: { totalRecords: data.length, totalPages: 1 }, links: { next: null, prev: null } };
    });
}

export type CreateWorkflowInput = {
  // Top-level field keys are camelCase; OliveBranch snake_cases them on the API.
  workflowDefinitionApiName: string;
  // One or more buildings; must be in the user's allowed_groups.
  groupIds: string[];
  // Optional seed of demographics, keyed by declared value name (snake_case).
  values?: WorkflowValuesInput;
  // Optional referral packet documents. Only new uploads on create — a referral being created
  // holds nothing to keep, and the API refuses an id here.
  attachments?: Upload[];
};

export async function createWorkflow({ attachments, ...params }: CreateWorkflowInput) {
  const body = { ...params, attachments: serializeAttachments(attachments) };
  return http.post<WorkflowOptions>(`workflows`, body).then((res) => new Workflow(res.data));
}

export async function showWorkflow(id: string, signal?: GenericAbortSignal) {
  return http.get<WorkflowOptions>(`workflows/${id}`, { signal }).then((res) => new Workflow(res.data));
}

// OLIO-2609: mint a fresh short-lived signed URL for this workflow's eligibility PDF (served by
// olio-api, referral-scoped). Called on link click; the URL is opened in a new tab.
// `facetId` names WHICH coverage's PDF (OLIO-2793). The verification runs per coverage, so the
// referral holds one result per coverage — the endpoint reads one named slot and answers
// "unavailable" rather than guessing. Omitted for a referral pinned to a pre-coverage
// configuration, where the result lives at referral level.
// `eligibilityCheckId` names ONE PAST RUN instead (OLIO-2794's History): a re-run retires the previous
// result, so a coverage's current row is only its latest check. The API matches the id against this
// referral's own results — current and retired — so a History row downloads the run it names.
export async function getEligibilityPdfUrl(
  workflowId: string,
  facetId?: string | null,
  eligibilityCheckId?: string | null
) {
  const params = eligibilityCheckId
    ? { eligibility_check_id: eligibilityCheckId }
    : facetId
      ? { facet_id: facetId }
      : undefined;

  return http
    .get<{ url: string; filename?: string }>(`workflows/${workflowId}/eligibility_pdf`, { params })
    .then((res) => res.data);
}

// Every benefit verification this referral has run, newest first (OLIO-2794's History tab). Its own
// request rather than part of the referral payload: that payload is polled every few seconds and
// carries only current values, so past results don't ride along on all of them.
export interface VerificationRun {
  id: string;
  // Which coverage ran. Null for a referral whose verification is still referral-level.
  facetId: string | null;
  completedAt: string;
  // Who set this run going, in the same actor shape the activity log uses: `user` is a person who asked
  // for it, `olio` the engine having started it itself. Both null when no initiating entry survives —
  // the API names nobody rather than the wrong somebody.
  ranBy: { name: string | null; kind: 'user' | 'olio' | 'integration' | null };
  // Absent when that run produced no downloadable document (an error, or a payer not enrolled).
  eligibilityCheckId: string | null;
}

export async function indexVerificationHistory(workflowId: string, signal?: GenericAbortSignal) {
  return http
    .get<{ data: VerificationRun[] }>(`workflows/${workflowId}/verification_history`, { signal })
    .then((res) => res.data.data);
}

// One coverage's own values (OLIO-2793). Insurance details belong to a coverage rather than to the
// referral, and a coverage-scoped value is never read at referral level — so correcting a payer name
// or member ID goes to the coverage, not through the referral's `values`.
//
// PATCH rather than re-writing the referral's `coverages` list: editing that list is a manual write
// keyed on identity, so changing a payer name would mint a SECOND coverage rather than correct the
// one in front of you. Editing the coverage itself corrects it in place — which is the correction
// path the API is built around.
export type UpdateFacetInput = {
  workflowId: string;
  facetId: string;
  values: WorkflowValuesInput;
};

export async function updateFacet({ workflowId, facetId, values }: UpdateFacetInput) {
  return http
    .patch<WorkflowOptions>(`workflows/${workflowId}/facets/${facetId}`, { values })
    .then((res) => new Workflow(res.data));
}

// A coverage a person added by hand (OLIO-2794): the API records the coverage plus its values, and the
// engine creates that coverage's payer search and verification on its next pass — so there's nothing to
// dispatch from here.
//
// `dimension` is rejected unless the referral's own configuration declares it, which is what keeps this
// off referrals pinned to a version from before coverages existed.
export type CreateFacetInput = {
  workflowId: string;
  dimension: string;
  values: WorkflowValuesInput;
};

export async function createFacet({ workflowId, dimension, values }: CreateFacetInput) {
  return http
    .post<WorkflowOptions>(`workflows/${workflowId}/facets`, { dimension, values })
    .then((res) => new Workflow(res.data));
}

// One action a person's single gesture completes (OLIO-3022). `outputs` is the same free-form
// hash the per-action endpoint takes — keyed by declared output name, sent verbatim.
export type ActionCompletionInput = {
  id: string;
  outputs: WorkflowValuesInput;
};

// Editing a referral updates the workflow itself — buildings, demographics, and the
// packet. Only the keys provided are applied (the API rejects an empty update).
export type UpdateWorkflowInput = {
  workflowId: string;
  groupIds?: string[];
  values?: WorkflowValuesInput;
  // The full packet after the person's edits — kept documents by id plus new uploads. Undefined
  // when the packet wasn't touched; `[]` when they removed all of it.
  attachments?: PacketEntry[];
  // The consent cascade (design §9): the action ids the user approved restarting when
  // saving this correction. The API resets each one (instance + its produced values) in
  // the same write; a stale id rejects the whole save with a restart_action_ids error.
  restartActionIds?: string[];
  // The by-property slideout's single submit: every action the person's one gesture resolves,
  // applied inside the same transaction as the values above. Resolve-all-then-apply on the API —
  // a stale id or outputs a kind rejects fails the whole request rather than half-committing the
  // gesture, which is why this is one call and not a loop over the per-action endpoint.
  actionCompletions?: ActionCompletionInput[];
};

export async function updateWorkflow({
  workflowId,
  groupIds,
  values,
  attachments,
  restartActionIds,
  actionCompletions,
}: UpdateWorkflowInput) {
  const body = {
    groupIds,
    values,
    attachments: serializeAttachments(attachments),
    actionCompletions: actionCompletions && actionCompletions.length > 0 ? actionCompletions : undefined,
    // Omit when empty — an empty array is not a consent list.
    restartActionIds: restartActionIds && restartActionIds.length > 0 ? restartActionIds : undefined,
  };
  return http.put<WorkflowOptions>(`workflows/${workflowId}`, body).then((res) => new Workflow(res.data));
}

// Archiving a referral is a state transition, so it rides the same update endpoint as an explicit
// intent rather than a route of its own (OLIO-2599). `cancel` is the engine's word for it; the
// product calls it Archive, and the wire keeps the engine's because the same transition happens
// without anyone archiving anything (a confirmed duplicate).
export async function archiveWorkflow(workflowId: string) {
  return http
    .put<WorkflowOptions>(`workflows/${workflowId}`, { invokeAction: 'cancel' })
    .then((res) => new Workflow(res.data));
}

export type CompleteWorkflowActionInput = {
  workflowId: string;
  actionId: string;
  // Keyed by declared output name (snake_case), e.g. { clinical_determination: 'meets_criteria' }.
  outputs: WorkflowValuesInput;
};

export async function completeWorkflowAction({ workflowId, actionId, outputs }: CompleteWorkflowActionInput) {
  // `complete` is sent explicitly — the API never assumes it, so a missing intent can't
  // silently finalize an action (it 422s).
  return http
    .patch<WorkflowOptions>(`workflows/${workflowId}/actions/${actionId}`, { invokeAction: 'complete', outputs })
    .then((res) => new Workflow(res.data));
}

export type CompleteWorkflowActionsInput = {
  workflowId: string;
  // Every id shares the one `outputs` hash, so the API rejects a batch that mixes action kinds.
  actionIds: string[];
  outputs: WorkflowValuesInput;
};

// The collection route (no action id in the path): one decision over a selection is ONE request,
// so the domain records the moment once instead of leaving N completions to be reassembled
// afterward. A single row's decision is the one-element case of the same call.
export async function completeWorkflowActions({ workflowId, actionIds, outputs }: CompleteWorkflowActionsInput) {
  return http
    .patch<WorkflowOptions>(`workflows/${workflowId}/actions`, { invokeAction: 'complete', actionIds, outputs })
    .then((res) => new Workflow(res.data));
}

// One activity-feed entry (design §10): the narrated story, oldest-first. `createdBy`
// drives the User / Olio / Integration chip and the "by <name>" line.
export interface WorkflowLogEntry {
  id: string;
  event: string;
  summary: string;
  detail: string | null;
  data: Record<string, unknown>;
  createdAt: string;
  workflowActionId: string | null;
  groupId: string | null;
  createdBy: { id: string; name: string; kind: 'user' | 'olio' | 'integration' };
}

export async function indexWorkflowLogs(workflowId: string, signal?: AbortSignal): Promise<WorkflowLogEntry[]> {
  return http
    .get<{ data: WorkflowLogEntry[] }>(`workflows/${workflowId}/logs`, { signal })
    .then((res) => res.data.data);
}

// Amend a completed step's recorded outputs (the decided-row menu: change decision /
// add note) — record-keeping semantics: the API retires the prior record as history and
// completes a fresh instance with these outputs, atomically.
export async function amendWorkflowAction({ workflowId, actionId, outputs }: CompleteWorkflowActionInput) {
  return http
    .patch<WorkflowOptions>(`workflows/${workflowId}/actions/${actionId}`, { invokeAction: 'amend', outputs })
    .then((res) => new Workflow(res.data));
}

// `save_progress` records (stages) the outputs on the action WITHOUT completing it — the
// hand-off flow uses it when retrieved values still need a person's confirmation.
//
// `narrationHint` is an optional signal the caller can send when the intent behind a bulk write
// isn't recoverable from the diff alone. SexOffenderCheck uses it for "Dismiss All <bucket>" so
// the activity-log entry reads "Dismissed all Strong results" instead of the per-card phrasing
// the narrator would otherwise emit. Server validates it against a whitelist (Rails constants
// per-kind), so the FE can't inject arbitrary log text.
export type SaveWorkflowActionProgressInput = CompleteWorkflowActionInput & {
  narrationHint?: string;
};

export async function saveWorkflowActionProgress({
  workflowId,
  actionId,
  outputs,
  narrationHint,
}: SaveWorkflowActionProgressInput) {
  return http
    .patch<WorkflowOptions>(`workflows/${workflowId}/actions/${actionId}`, {
      invokeAction: 'save_progress',
      outputs,
      ...(narrationHint ? { narrationHint } : {}),
    })
    .then((res) => new Workflow(res.data));
}

// Start a user-dispatched action's work now (OLIO-2846). A separate call rather than a
// WorkflowActionIntent value because it carries `outputs`, and because it is semantically unlike
// rerun/dismiss: those act on work the engine already scheduled, while `dispatch` is the only way to
// start work at all on a kind that waits for a person's click.
//
// It is the FIRST send only. The API's DISPATCHABLE_FROM is `pending`/`action_required` — `errored`
// and `completed` are deliberately absent, so a retry or a re-send to a different facility goes
// through `resendWorkflowAction` below.
//
// `outputs` is staged, not completed: it carries the documents selected for THIS send. The action's
// eventual completion replaces `outputs` wholesale, which is fine — the durable copy lives on the
// platform's own record and its S3 audit.
export async function dispatchWorkflowAction({ workflowId, actionId, outputs }: DispatchWorkflowActionInput) {
  return http
    .patch<WorkflowOptions>(`workflows/${workflowId}/actions/${actionId}`, { invokeAction: 'dispatch', outputs })
    .then((res) => new Workflow(res.data));
}

export type DispatchWorkflowActionInput = {
  workflowId: string;
  actionId: string;
  outputs?: WorkflowValuesInput;
};

// Send again after an attempt that already finished — a failed push, or a successful one whose
// facility is no longer the selected one. Two calls, in this order, because that is what the engine's
// model of an attempt requires (design 010): `rerun` retires the finished instance and mints a fresh
// one, and `dispatch` sends THAT instance.
//
// One attempt per instance is the point, not a formality. Re-dispatching the same row would make two
// attempts indistinguishable on the wire, so a late callback from the first would be applied to the
// second — the engine drops a result addressed to a no-longer-current instance, and that only works
// if each attempt has its own.
//
// The fresh instance is located by kind + scope rather than by a lineage pointer, and the scope is
// read from the CALLER's action rather than from the response: `rerun` retires the old instance out
// of the current set, so looking it up in the response to learn its own kind finds nothing. The API
// serves `rerun` as a boolean rather than `previous_id` itself.
//
// Matching on `groupId` is what makes this correct now the sync is PER_GROUP — a run carries one
// current row per targeted building, so kind alone would be ambiguous. The API's own rerun passes the
// group through for the same reason; without it, run_action looks for an ungrouped prior and mints an
// orphan with no lineage.
//
// If the rerun produced nothing to send to, fail loudly. Falling back to the original id would
// re-dispatch a retired instance and surface as a confusing 422 with no hint of what went wrong.
export async function resendWorkflowAction({ workflowId, action, outputs }: ResendWorkflowActionInput) {
  const afterRerun = await invokeWorkflowAction({ workflowId, actionId: action.id, invokeAction: 'rerun' });
  const fresh = afterRerun.actions.find(
    (a) => a.id !== action.id && a.actionType === action.actionType && a.groupId === action.groupId
  );

  if (!fresh) throw new Error('Could not start a new attempt — reload the referral and try again.');

  return dispatchWorkflowAction({ workflowId, actionId: fresh.id, outputs });
}

export type ResendWorkflowActionInput = {
  workflowId: string;
  action: Pick<WorkflowAction, 'id' | 'actionType' | 'groupId'>;
  outputs?: WorkflowValuesInput;
};

export type WorkflowActionIntent = 'rerun' | 'dismiss';

export type InvokeWorkflowActionInput = {
  workflowId: string;
  actionId: string;
  invokeAction: WorkflowActionIntent;
};

export async function invokeWorkflowAction({ workflowId, actionId, invokeAction }: InvokeWorkflowActionInput) {
  return http
    .patch<WorkflowOptions>(`workflows/${workflowId}/actions/${actionId}`, { invokeAction })
    .then((res) => new Workflow(res.data));
}

export type MarkWorkflowActionViewedInput = {
  workflowId: string;
  actionId: string;
};

// Records that the acting user viewed this action's results — the API stamps the current user
// + timestamp, bumps the view count (drives the "Viewed by …" attribution), and completes the
// action (action_required → completed). No request body; returns only the view attribution
// (not the settled workflow), so callers refetch the workflow to pick up the change.
export async function markWorkflowActionViewed({ workflowId, actionId }: MarkWorkflowActionViewedInput) {
  return http.post<WorkflowActionView>(`workflows/${workflowId}/actions/${actionId}/views`).then((res) => res.data);
}
